import { useMemo, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';

import { categoryLabel } from '../lib/category';
import { filterBusinesses } from '../lib/roster';
import { HqIcon } from './HqIcon';
import { initialsOf } from './nav';
import { useBusinessesQuery } from './queries';

const MAX_RESULTS = 6;

/**
 * "Buscar clientes" in the top bar: the businesses whose name has what was
 * typed, accents and capitals aside. Picking one opens it; Enter takes the
 * search to the full list.
 */
export function HqSearch() {
  const navigate = useNavigate();
  const businessesQuery = useBusinessesQuery();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const text = query.trim();
  const results = useMemo(
    () => (text ? filterBusinesses(businessesQuery.data?.data ?? [], text, 'todos').slice(0, MAX_RESULTS) : []),
    [businessesQuery.data, text],
  );

  function close() {
    setOpen(false);
    setQuery('');
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') setOpen(false);
    if (event.key === 'Enter' && text) {
      event.preventDefault();
      void navigate({ to: '/admin/clientes', search: { q: text } });
      close();
    }
  }

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    // Leaving for one of the results is not leaving the search.
    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
  }

  return (
    <div className="hq-search" onBlur={handleBlur}>
      <HqIcon name="buscar" size={18} />
      <input
        type="search"
        aria-label="Buscar clientes"
        placeholder="Buscar clientes"
        value={query}
        autoComplete="off"
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
      />
      {open && text ? (
        results.length > 0 ? (
          <ul className="hq-search-results" aria-label="Resultados de clientes">
            {results.map((business) => (
              <li key={business.id}>
                <Link to="/admin/negocios/$businessId" params={{ businessId: business.id }} onClick={close}>
                  <span className="hq-avatar" aria-hidden="true">
                    {initialsOf(business.name)}
                  </span>
                  <span className="hq-search-text">
                    <strong>{business.name}</strong>
                    <small>
                      {categoryLabel(business.category)} · {business.neighborhood}
                    </small>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : businessesQuery.isSuccess ? (
          <p className="hq-search-empty" role="status">
            Ningún cliente se llama así.
          </p>
        ) : null
      ) : null}
    </div>
  );
}
